import { useEffect, useState } from "react"
import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom"
import { HelmetProvider } from 'react-helmet-async'
import { localization } from "./utils/text.js"
import HomePage from "./routes/HomePage.js"
import ErrorPage from "./routes/ErrorPage.js"
import RouterRoot from "./routes/RouterRoot.js"
import FamilyPage from "./routes/FamilyPage.js"
import HistoryPage from "./routes/HistoryPage.js"
import AnimalsPage from "./routes/AnimalsPage.js"
import EnvironmentPage from "./routes/EnvironmentPage.js"
import ApartmentsPage from "./routes/ApartmentsPage.js"
import PricesPage from "./routes/PricesPage.js"
import ServicesPage from "./routes/ServicesPage.js"
import SheepPage from "./routes/SheepPage.js"
import ArrivalPage from "./routes/ArrivalPage.js"
import ContactPage from "./routes/ContactPage.js"
import BookRoomPage from "./routes/BookRoomPage.js"
import PrivacyPage from "./routes/PrivacyPage.js"
import LegalInformationPage from "./routes/LegalInformationPage.js"
import SitemapPage from "./routes/SitemapPage.js"
import LanguageRoute from "./components/LanguageRoute.js"
import { systemTranslations } from "./utils/systemTranslations.js"

const initialSettings = {
    language: "de-DE",
    dataBsTheme: "light",
    cookiesAccepted: false,
    acceptOnlyNecessaryCookies: true,
}

const initialLocalText = {
    ...systemTranslations,
    "de-DE": [],
    "it-IT": [],
    "en-US": []
}

const getPreferredLanguage = () => {
    const userLanguage = navigator.language || navigator.userLanguage
    let lang = "de" // default to German

    if (userLanguage) {
        switch (userLanguage.toLowerCase()) {
            case "it":
            case "it-it":
                lang = "it"
                break
            case "de":
            case "de-at":
            case "de-de":
            case "de-ch":
                lang = "de"
                break
            case "en":
            case "en-us":
            case "en-gb":
                lang = "en"
                break
            default:
                lang = "en"
                break
        }
    }
    return lang
}

const App = () => {
    const [settings, setSettings] = useState(initialSettings)
    const [text, setText] = useState([])
    const [localizedText, setLocalizedText] = useState(initialLocalText)

    useEffect(() => {
        const preferredLang = getPreferredLanguage()
        const languageMap = {
            'de': 'de-DE',
            'it': 'it-IT',
            'en': 'en-US'
        }

        setSettings({
            ...settings,
            language: languageMap[preferredLang],
        })
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        // Handle Website Theme
        const element = document.getElementsByTagName('html')
        if (element.length > 0) {
            element[0].setAttribute('data-bs-theme', settings.dataBsTheme)
        }

        // Handle Website Language
        if (localizedText[settings.language].length <= 0) {
            localization(settings.language).then((texts) => setLocalizedText({
                ...localizedText,
                [settings.language]: texts,
            }))
            setText(localizedText[settings.language])
        } else {
            setText(localizedText[settings.language])
        }
    }, [settings.language, localizedText])

    const router = createBrowserRouter([
        {
            path: "/",
            element: <Navigate to={`/${getPreferredLanguage()}`} replace />,
        },
        {
            path: "/:lang",
            element: <RouterRoot text={text} settings={settings} setSettings={setSettings} />,
            errorElement: <ErrorPage />,
            children: [
                {
                    path: "",
                    element: <LanguageRoute settings={settings} setSettings={setSettings} />,
                    children: [
                        { path: "", element: <HomePage text={text} /> },
                        { path: "family", element: <FamilyPage text={text} /> },
                        { path: "history", element: <HistoryPage text={text} /> },
                        { path: "animals", element: <AnimalsPage text={text} /> },
                        { path: "environment", element: <EnvironmentPage text={text} settings={settings} /> },
                        { path: "apartments", element: <ApartmentsPage text={text} /> },
                        { path: "prices", element: <PricesPage text={text} /> },
                        { path: "services", element: <ServicesPage settings={settings} text={text} /> },
                        { path: "sheep", element: <SheepPage text={text} /> },
                        { path: "arrival", element: <ArrivalPage text={text} settings={settings} setSettings={setSettings} /> },
                        { path: "contact", element: <ContactPage text={text} /> },
                        { path: "bookroom", element: <BookRoomPage text={text} settings={settings} /> },
                        { path: "privacy", element: <PrivacyPage text={text} /> },
                        { path: "legalinfo", element: <LegalInformationPage text={text} /> },
                        { path: "sitemap", element: <SitemapPage text={text} language={settings.language} /> },
                    ]
                }
            ]
        }
    ])

    return (
        <HelmetProvider>
            <RouterProvider router={router} />
        </HelmetProvider>
    )
}

export default App
